import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  catalogBySlug,
  coerceLocale,
  pickLocalizedText,
  type Exercise,
} from "@jackedlogs/shared";

// Resolve an exercise display name BY SLUG against the live catalog
// (shared mirror of mobile's useExerciseNameBySlug). The architectural
// rule (Antenore 2026-05-22): exercise names are NEVER read from a
// write-time snapshot; always resolved via foreign key + the live
// catalog, so a rename propagates everywhere without a migration. When
// the catalog row is missing (deleted exercise) fall back to the optional
// snapshot, then a localised placeholder.
// [[feedback_no_denormalized_display_snapshots]]
export function useExerciseNameResolver(
  catalog: ReadonlyArray<Exercise> | null | undefined,
): (slug: string, snapshotFallback?: string) => string {
  const { i18n, t } = useTranslation();
  const locale = coerceLocale(i18n.language);
  // Memo the Map identity so callers can safely include the returned
  // closure in their useMemo deps without busting every render.
  const map = useMemo(() => catalogBySlug(catalog ?? []), [catalog]);
  return useCallback(
    (slug, snapshotFallback) => {
      const ex = map?.get(slug);
      if (ex) return pickLocalizedText(ex.name, locale);
      if (snapshotFallback && snapshotFallback.trim().length > 0)
        return snapshotFallback;
      return t("catalog.deletedExercise", {
        defaultValue: "Deleted exercise",
        slug,
      });
    },
    [map, locale, t],
  );
}
